/**
 * Full-page booking flow for one quote: details, review, then confirmation.
 * The form lives here so Edit from the review returns to what was typed.
 */
import React, { useEffect, useState } from 'react';

import type { NormalizedQuote } from '../../../apis/lanePlanner';
import BookingConfirmation from './BookingConfirmation';
import BookingFormStep from './BookingFormStep';
import {
  BookingErrors,
  emptyBookingForm,
  toBookingRequest,
  validateBooking,
} from './bookingForm';
import BookingReviewStep from './BookingReviewStep';
import BookingSummary from './BookingSummary';
import { travelTextOf } from './quoteView';
import styles from './styles.module.css';
import { isConfirming, useLaneBooking } from './useLaneBooking';

interface BookingPageProps {
  token: string;
  quote: NormalizedQuote;
  vendorName: string;
  origin: string;
  destination: string;
  onBackToResults: () => void;
  /** True while a booking is being sent or confirmed, so the page is not torn down under it. */
  onBusyChange?: (busy: boolean) => void;
  onViewShipments: () => void;
  onPlanAnother: () => void;
}

type Step = 'details' | 'review';

const BookingPage: React.FC<BookingPageProps> = ({
  token,
  quote,
  vendorName,
  origin,
  destination,
  onBackToResults,
  onBusyChange,
  onViewShipments,
  onPlanAnother,
}) => {
  const [step, setStep] = useState<Step>('details');
  const [values, setValues] = useState(() =>
    emptyBookingForm(origin, destination)
  );
  const [errors, setErrors] = useState<BookingErrors>({});
  const { booking, stalled, submitting, error, submit } = useLaneBooking(token);
  const busy = submitting || (isConfirming(booking) && !stalled);

  useEffect(() => {
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);

  const toReview = (): void => {
    const found = validateBooking(values, quote);
    setErrors(found);
    if (Object.keys(found).length === 0) setStep('review');
  };

  if (booking) {
    return (
      <div className={styles.bookingShell}>
        <BookingConfirmation
          booking={booking}
          stalled={stalled}
          departureZone={quote.route?.origin_timezone}
          transit={travelTextOf(quote)}
          onViewShipments={onViewShipments}
          onPlanAnother={onPlanAnother}
        />
      </div>
    );
  }

  return (
    <div className={styles.bookingShell}>
      <div className={styles.bookingMain}>
        <button
          type='button'
          className={styles.linkButton}
          disabled={submitting}
          onClick={onBackToResults}
        >
          ← Back to results
        </button>
        <h3 className={styles.bookingTitle}>Booking</h3>
        {step === 'details' ? (
          <BookingFormStep
            quote={quote}
            values={values}
            errors={errors}
            onChange={setValues}
            onBack={onBackToResults}
            onContinue={toReview}
          />
        ) : (
          <BookingReviewStep
            quote={quote}
            values={values}
            submitting={submitting}
            error={error}
            onEdit={() => setStep('details')}
            onConfirm={() => submit(toBookingRequest(quote, values))}
          />
        )}
      </div>
      <BookingSummary quote={quote} vendorName={vendorName} />
    </div>
  );
};

export default BookingPage;
